import React from 'react';
import { FormComponent, FormComponentProps } from '../index';
import { useTranslation } from 'react-i18next';
import { parseError } from '../../../services/utils';
import { Controller, useFormContext } from 'react-hook-form';
import FormField from '../../common/form-field';
import inputCss from './read-input.module.css';
import './amount.css';
import { Option, SelectController } from './select';
import { ObservableOptions } from './select/async-select';

type AmountProps = {
  asyncOptions?: ObservableOptions;
  options?: Option[];
  label?: string;
  cacheOn?: any[] | ((parentName: string) => string[]);
  onDisabled?: 'amount' | 'unit' | 'both';
  disableAmount?: boolean;
  disableUnit?: boolean;
  clearable?: boolean;
  explanation?: string;
  inline?: boolean;
  disabled?: boolean;
  column?: boolean;
  requiredShow?: boolean;
};

export default function Amount(
  names: {
    amount: string;
    unit: string;
    maxAllowedAmountRefName?: string;
  },
  properties: AmountProps = {},
  horizontal?: boolean,
  requiredShow?: boolean
): FormComponent {
  return ({ ...rest }) => (
    <AmountComponent
      name={[
        names?.amount ?? 'amount',
        names?.unit ?? 'unit',
        names?.maxAllowedAmountRefName ?? null,
      ]}
      label={null}
      cacheOn={[]}
      inline={false}
      horizontal={horizontal}
      requiredShow={requiredShow}
      {...properties}
      {...rest}
    />
  );
}

export function AmountComponent({
  name,
  label,
  parentName,
  readonly,
  disabled,
  required,
  asyncOptions,
  options,
  clearable,
  cacheOn,
  onDisabled,
  disableAmount,
  disableUnit,
  explanation,
  inline,
  horizontal,
  column,
  requiredShow = false,
}: FormComponentProps &
  AmountProps & {
    name: [string, string, string?];
    label: string;
    required?: string[];
  }) {
  const { t } = useTranslation();
  const {
    register,
    getValues,
    formState: { errors },
  } = useFormContext();
  const [amount, unit, maxAllowedRefName] = name;
  const inputName = parentName ? `${parentName}.${amount}` : amount;
  const selectNameRo = parentName ? `${parentName}.${unit}` : unit;
  const refName = maxAllowedRefName
    ? parentName
      ? `${parentName}.${maxAllowedRefName}`
      : maxAllowedRefName
    : null;
  const selectName = unit;
  const inputError = parseError(inputName, errors);
  const selectError =
    parseError(selectName, errors) || parseError(selectNameRo, errors);
  const warningMsg =
    refName && getValues(inputName) > getValues(refName)
      ? 'The specified product does not have sufficient inventory balance'
      : null;

  if (readonly) {
    return (
      <FormField
        inline={inline}
        label={label ? t(label) : t(amount)}
        labelFor={inputName}
        horizontal={horizontal}
        column={column}
      >
        <div className="amount-container amount-container--readonly">
          <div className="amount-input--readonly">
            <Controller
              name={inputName}
              defaultValue={''}
              render={({ field: { value } }) => {
                const size = value.toString().length;

                return (
                  <input
                    readOnly={true}
                    size={size}
                    type="text"
                    value={value}
                    className={`amount-input--readonly ${inputCss['readonly']}`}
                    name={inputName}
                  />
                );
              }}
            />
          </div>

          <div className="amount-select--readonly">
            <Controller
              name={selectNameRo}
              defaultValue={null}
              render={({ field: { value } }) => {
                if (typeof value === 'object') {
                  value = value?.label;
                }

                return (
                  <input
                    readOnly={true}
                    type="text"
                    className={`amount-select--readonly-XXX ${inputCss['readonly']}`}
                    value={value ?? ''}
                    name={selectNameRo}
                  />
                );
              }}
            />
          </div>
        </div>
      </FormField>
    );
  } else {
    return (
      <FormField
        inline={inline}
        column={column}
        label={label ? t(label) : t(amount)}
        labelFor={inputName}
        className={`amount-field ${selectError ? 'has-select-error' : ''}`}
        required={required?.includes(inputName) || requiredShow}
        error={[inputError?.message, selectError?.value?.message, warningMsg]}
        horizontal={horizontal}
      >
        <div className="amount-container">
          <input
            type="number"
            name={inputName}
            min="0"
            step="any"
            className="amount-input"
            {...register(inputName)}
            readOnly={readonly}
            disabled={
              ['amount', 'both'].includes(onDisabled)
                ? disabled || disableAmount
                : false
            }
          />

          <div className="amount-select">
            <SelectController
              multi={false}
              disabled={
                ['unit', 'both'].includes(onDisabled)
                  ? disabled || disableUnit
                  : false
              }
              clearable={clearable}
              asyncOptions={asyncOptions}
              options={options}
              cacheOn={cacheOn}
              name={selectName}
              parentName={parentName}
            />
          </div>
        </div>
        {explanation && <div className="explanation">{explanation}</div>}
      </FormField>
    );
  }
}
